import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import {
  Coins, CheckCircle2, XCircle, Clock, Loader2, Search,
  ExternalLink, Receipt, DollarSign, Eye,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

const STATUS_CONFIG = {
  pending: { label: 'En revisión', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  approved: { label: 'Aprobado', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 },
  rejected: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card className="bg-gray-900/80 border-gray-800" data-testid={`stat-${label.toLowerCase()}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-black text-white">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default function AdminCreditOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [reviewAction, setReviewAction] = useState('approve');
  const [adminNote, setAdminNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/admin/credits/orders${params}`, { headers: headers() });
      setOrders(res.data.orders || []);
      setStats(res.data.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (e) {
      toast.error('Error al cargar órdenes');
    }
    setLoading(false);
  }, [headers, filter]);

  useEffect(() => { if (token) fetchOrders(); }, [token, fetchOrders]);

  const openReview = (order, action) => {
    setReviewing(order);
    setReviewAction(action);
    setAdminNote('');
  };

  const submitReview = async () => {
    if (!reviewing) return;
    setProcessing(true);
    try {
      const url = `${API}/admin/credits/orders/${reviewing.id}/${reviewAction}`;
      const res = await axios.post(url, { admin_note: adminNote || null }, { headers: headers() });
      toast.success(res.data.message);
      setReviewing(null);
      setAdminNote('');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
    setProcessing(false);
  };

  const filtered = orders.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      o.reference?.toLowerCase().includes(s) ||
      o.user_email?.toLowerCase().includes(s) ||
      o.user_name?.toLowerCase().includes(s)
    );
  });

  return (
    <AdminLayout title="Órdenes de Créditos" subtitle="Verificar pagos y acreditar a usuarios">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Pendientes" value={stats.pending} icon={Clock} color="bg-amber-500/20 text-amber-400" />
        <StatCard label="Aprobadas" value={stats.approved} icon={CheckCircle2} color="bg-green-500/20 text-green-400" />
        <StatCard label="Rechazadas" value={stats.rejected} icon={XCircle} color="bg-red-500/20 text-red-400" />
        <StatCard label="Total" value={stats.total} icon={Receipt} color="bg-purple-500/20 text-purple-400" />
      </div>

      {/* Filters */}
      <Card className="bg-gray-900 border-gray-800 mb-4">
        <CardContent className="p-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por email, referencia o nombre..."
              className="pl-9 bg-gray-950 border-gray-800 text-white"
              data-testid="search-orders"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-[160px] bg-gray-950 border-gray-800 text-white" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value="all" className="text-white hover:bg-gray-800">Todos</SelectItem>
              <SelectItem value="pending" className="text-white hover:bg-gray-800">Pendientes</SelectItem>
              <SelectItem value="approved" className="text-white hover:bg-gray-800">Aprobadas</SelectItem>
              <SelectItem value="rejected" className="text-white hover:bg-gray-800">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Orders list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-amber-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="py-12 text-center">
            <Coins size={40} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">No hay órdenes {filter !== 'all' ? STATUS_CONFIG[filter]?.label?.toLowerCase() : ''}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {filtered.map((o) => {
            const st = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending;
            const Icon = st.icon;
            return (
              <Card key={o.id} className="bg-gray-900 border-gray-800" data-testid={`order-${o.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* User */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={st.color}>
                          <Icon size={10} className="mr-1" /> {st.label}
                        </Badge>
                        <span className="text-amber-400 font-bold flex items-center gap-1">
                          <DollarSign size={14} />{o.amount_usd}
                        </span>
                        <span className="text-cyan-300 font-bold text-sm">+{o.credits_to_add} créditos</span>
                      </div>
                      <div className="text-white font-medium text-sm truncate">{o.user_name || 'Usuario'} · {o.user_email}</div>
                      <div className="text-slate-500 text-xs flex items-center gap-3 mt-1 flex-wrap">
                        <span className="font-mono">Ref: {o.reference}</span>
                        <span>{new Date(o.created_at).toLocaleString('es-DO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      {o.admin_note && (
                        <p className="mt-1 text-[10px] text-slate-500 italic">Nota admin: {o.admin_note}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {o.proof_image && (
                        <Button size="sm" variant="outline" onClick={() => setViewing(o)} className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" data-testid={`view-proof-${o.id}`}>
                          <Eye size={14} className="mr-1" /> Comprobante
                        </Button>
                      )}
                      {o.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => openReview(o, 'approve')} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${o.id}`}>
                            <CheckCircle2 size={14} className="mr-1" /> Aprobar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openReview(o, 'reject')} className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`reject-${o.id}`}>
                            <XCircle size={14} className="mr-1" /> Rechazar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Proof viewer */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comprobante {reviewing?.payment_method === 'cash_app' ? 'Cash App' : reviewing?.payment_method === 'paypal' ? 'PayPal' : 'Zelle'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Ref: <span className="font-mono text-amber-400">{viewing?.reference}</span> · ${viewing?.amount_usd}
            </DialogDescription>
          </DialogHeader>
          {viewing?.proof_image && (
            <img src={viewing.proof_image} alt="proof" className="max-h-[60vh] mx-auto rounded-lg border border-gray-800" />
          )}
          {viewing?.note && (
            <p className="text-sm text-slate-400 italic">Nota del usuario: {viewing.note}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewing} onOpenChange={(v) => !v && setReviewing(null)}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className={reviewAction === 'approve' ? 'text-green-400' : 'text-red-400'}>
              {reviewAction === 'approve' ? 'Aprobar orden' : 'Rechazar orden'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {reviewAction === 'approve'
                ? `Confirma que verificaste el pago de $${reviewing?.amount_usd} en ${reviewing?.payment_method === 'cash_app' ? 'Cash App' : reviewing?.payment_method === 'paypal' ? 'PayPal' : 'Zelle'}. Se acreditarán ${reviewing?.credits_to_add} créditos al usuario.`
                : 'Indica el motivo del rechazo (opcional).'}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Nota (opcional)</label>
            <Textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder={reviewAction === 'approve' ? 'Verificado en la cuenta receptora, ref XXX' : 'Comprobante no coincide con monto, etc.'}
              className="bg-gray-900 border-gray-700 text-white"
              rows={3}
              data-testid="admin-note-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewing(null)} className="border-gray-700 text-gray-300 hover:bg-gray-800">
              Cancelar
            </Button>
            <Button
              onClick={submitReview}
              disabled={processing}
              className={reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-review-btn"
            >
              {processing ? <Loader2 size={16} className="animate-spin mr-1" /> : (reviewAction === 'approve' ? <CheckCircle2 size={16} className="mr-1" /> : <XCircle size={16} className="mr-1" />)}
              Confirmar {reviewAction === 'approve' ? 'aprobación' : 'rechazo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
